import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, AlertCircle, X, RefreshCw } from 'lucide-react';
import GlassButton from '../ui/GlassButton';
import {
  formIntakeApi,
  FormIntakeForm,
  FormIntakeFieldDiff,
} from '../../services/api';
import './FormIntake.css';

// ─── helpers ────────────────────────────────────────────────────────────────

// Map API errors to friendly copy, calling out the two contract cases explicitly:
//   409 → remote provisioning not configured/enabled for this company yet
//   502 → the customer's companion endpoint is unreachable
const diffError = (e: any): string => {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  const detailStr =
    typeof detail === 'string'
      ? detail
      : Array.isArray(detail)
        ? detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ')
        : '';
  if (status === 409) {
    return (
      detailStr ||
      "CRM publishing isn't configured for your instance yet. Contact support to enable remote field provisioning."
    );
  }
  if (status === 502) {
    return (
      detailStr ||
      "Couldn't reach your CRM to preview the changes. Your SuiteCRM instance may be offline — try again shortly or contact support."
    );
  }
  if (status === 400) {
    return detailStr || 'Map this form to a CRM parent module before publishing.';
  }
  return detailStr || 'Failed to load the field preview. Please try again.';
};

// ─── FieldDiffModal ───────────────────────────────────────────────────────────

interface FieldDiffModalProps {
  form: FormIntakeForm;
  // Runs the actual publish (backend write). Resolves on success; rejects to keep the modal open.
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

const FieldDiffModal: React.FC<FieldDiffModalProps> = ({ form, onConfirm, onClose }) => {
  const [diff, setDiff] = useState<FormIntakeFieldDiff | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState('');

  const isRemote = (form.target_crm ?? 'local') === 'company';

  const loadDiff = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await formIntakeApi.getFieldDiff(form.form_id);
      setDiff(data);
    } catch (err: any) {
      setError(diffError(err));
    } finally {
      setLoading(false);
    }
  }, [form.form_id]);

  useEffect(() => {
    loadDiff();
  }, [loadDiff]);

  const handleConfirm = async () => {
    setConfirming(true);
    setConfirmError('');
    try {
      await onConfirm();
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setConfirmError(
        typeof detail === 'string' ? detail : 'Publish failed. Please try again.'
      );
    } finally {
      setConfirming(false);
    }
  };

  const modules = diff ? Object.keys(diff) : [];
  const totalToCreate = diff
    ? modules.reduce((n, m) => n + (diff[m].to_create?.length ?? 0), 0)
    : 0;

  return (
    <div className="fi-diff-overlay" onClick={confirming ? undefined : onClose}>
      <div className="fi-diff-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fi-diff-header">
          <div>
            <h3 className="fi-diff-title">Review CRM changes before publishing</h3>
            <p className="fi-diff-subtitle">{form.name}</p>
          </div>
          <GlassButton variant="ghost" onClick={onClose} disabled={confirming} title="Close">
            <X size={16} />
          </GlassButton>
        </div>

        <div className="fi-diff-body">
          {loading ? (
            <div className="fi-diff-loading">
              <RefreshCw size={16} className="fi-spin" /> Loading field preview…
            </div>
          ) : error ? (
            <div className="fi-error">
              <AlertCircle size={16} />
              {error}
            </div>
          ) : (
            <>
              <div className="fi-diff-warning">
                <AlertTriangle size={18} />
                <span>
                  {isRemote
                    ? 'Publishing runs a live CRM rebuild on your own SuiteCRM instance to create the fields below. Your instance may briefly clear caches during the rebuild. This cannot be undone from here.'
                    : 'Publishing creates the custom fields below in SuiteCRM and runs a rebuild. This cannot be undone from here.'}
                </span>
              </div>

              {modules.length === 0 ? (
                <div className="fi-diff-empty-note">
                  No CRM modules are mapped for this form.
                </div>
              ) : totalToCreate === 0 ? (
                <div className="fi-diff-empty-note">
                  All fields already exist in the CRM — publishing will not create any new fields.
                </div>
              ) : null}

              {modules.map((module) => {
                const m = diff![module];
                const toCreate = m.to_create ?? [];
                const present = m.already_present ?? [];
                return (
                  <div key={module} className="fi-diff-module">
                    <h4 className="fi-diff-module-name">{module}</h4>

                    <div className="fi-diff-group">
                      <div className="fi-diff-group-label">
                        Fields to create ({toCreate.length})
                      </div>
                      {toCreate.length ? (
                        <div className="fi-diff-create">
                          {toCreate.map((f) => (
                            <span key={f} className="fi-diff-chip">{f}</span>
                          ))}
                        </div>
                      ) : (
                        <div className="fi-diff-empty-note">None — nothing new to add.</div>
                      )}
                    </div>

                    <div className="fi-diff-group">
                      <div className="fi-diff-group-label">
                        Already present ({present.length})
                      </div>
                      {present.length ? (
                        <div className="fi-diff-present">
                          {present.map((f) => (
                            <span key={f} className="fi-diff-chip">{f}</span>
                          ))}
                        </div>
                      ) : (
                        <div className="fi-diff-empty-note">None.</div>
                      )}
                    </div>
                  </div>
                );
              })}

              {confirmError && (
                <div className="fi-error">
                  <AlertCircle size={16} />
                  {confirmError}
                </div>
              )}
            </>
          )}
        </div>

        <div className="fi-diff-footer">
          <GlassButton variant="secondary" onClick={onClose} disabled={confirming}>
            Cancel
          </GlassButton>
          <GlassButton
            variant="primary"
            onClick={handleConfirm}
            disabled={loading || !!error || confirming}
          >
            {confirming ? 'Publishing…' : 'Confirm & Publish'}
          </GlassButton>
        </div>
      </div>
    </div>
  );
};

export default FieldDiffModal;
